import { LocalTimeExt, VisitedSectionExt } from '@castoredc/castor_prose';
import { submitQuestionnaireFunction } from 'features/firebase/firebase';
import {
  addPerformanceStartMark,
  addPerformanceEndMark,
} from 'features/monitoring/performanceMonitor';
import { selectParticipationDocData } from 'features/participations/store/participationSelectors';
import { getTimezoneName } from 'shared/utils/dateUtils';
import {
  getActivityTaskDocumentPath,
  prepareActivityTaskWriteData,
  updateActivityTaskDocument,
} from '../services/documentService';
import {
  callProseSubmissionRender,
  type ProsePostQuestionnaireOutput,
  type ProseSubmissionRenderResult,
} from '../services/proseService';
import { questionnaireAssetApiEndpoints } from './questionnaireAssetApiSlice';
import { enterPostQuestionnaire, setLoading } from './questionnaireSlice';
import type { FieldData, RenderContext } from '@castoredc/castor_prose';
import type {
  DbTask,
  DbTaskDataForUpdateOnScreenQFinish,
  DbTaskDataForUpdateOnScreenQPostQuestionnaireEnter,
  DbCalculationsData,
  DbTraceClientActionType,
  DbTraceProperties,
} from '@hub/common';
import type { RootState } from 'features/store/store';

/**
 * Runs PROSE submission render and returns the discriminated union result.
 * Throws if DSL is unavailable or PROSE fails — no fallbacks.
 */
export const resolveFinishData = async (
  state: RootState,
  docData: DbTask,
  contextData: RenderContext,
  now: Date,
  userTimezone: string
): Promise<{
  result: ProseSubmissionRenderResult;
  submissionContext: RenderContext;
}> => {
  const enrichedContext: RenderContext = {
    ...contextData,
    localTime: LocalTimeExt.fromDate(now, userTimezone),
    currentSection: VisitedSectionExt.end(),
  };

  const dslDefinition = selectParticipationDocData(state).manifest.definition;
  const { data: dslSource } =
    questionnaireAssetApiEndpoints.getProseDslSourceByAssetId.select(
      dslDefinition
    )(state);

  if (!dslSource) {
    throw new Error('DSL source not available at submission time');
  }

  const result = await callProseSubmissionRender({
    dslSource,
    questionnaireName: docData.questionnaireName,
    fieldsData: docData.fieldsData,
    contextData: enrichedContext,
  });

  // Persist the submission context (pre-submission context + end-of-visit
  // marker), not the post-submission result context, so the server-authoritative
  // submitQuestionnaire render reproduces the submission transition and re-fires
  // the on_submission triggers.
  return { result, submissionContext: enrichedContext };
};

/**
 * Persists PROSE submission render output to the activity task document.
 * Must be called while the task is still 'touched' or 'post-questionnaire' (Firestore security rules).
 */
export const writeQuestionnaireFinishData = async (
  userId: string,
  participationId: string,
  activityName: string,
  finishData: {
    contextData: RenderContext;
    fieldsData: FieldData | undefined;
    calculationsData: DbCalculationsData | undefined;
    pageIndex?: number;
  },
  traceProperties: Omit<DbTraceProperties, 'traceClientActionType'>,
  traceClientActionType: DbTraceClientActionType
): Promise<void> => {
  const questionnaireData = prepareActivityTaskWriteData({
    contextData: finishData.contextData,
    ...(finishData.fieldsData !== undefined && {
      fieldsData: finishData.fieldsData,
    }),
    ...(finishData.calculationsData !== undefined && {
      calculationsData: finishData.calculationsData,
    }),
    ...(finishData.pageIndex !== undefined && {
      pageIndex: finishData.pageIndex,
    }),
    ...traceProperties,
    traceClientActionType,
  } as
    | DbTaskDataForUpdateOnScreenQFinish
    | DbTaskDataForUpdateOnScreenQPostQuestionnaireEnter);

  addPerformanceStartMark('questionnaireDocumentUpdate');
  try {
    await updateActivityTaskDocument(
      getActivityTaskDocumentPath(userId, participationId, activityName),
      questionnaireData
    );
  } finally {
    addPerformanceEndMark('questionnaireDocumentUpdate', {
      console: true,
      detail: { actionName: traceClientActionType },
    });
  }
};

/**
 * Hands a finished questionnaire to the server, tagging the payload with the
 * 'screen-finish' client action. The server owns the terminal status and dedupes
 * a resumed re-invocation.
 */
export const submitQuestionnaireToServer = async ({
  participationId,
  activityName,
  traceProperties,
}: {
  participationId: string;
  activityName: string;
  traceProperties: Omit<DbTraceProperties, 'traceClientActionType'>;
}): Promise<void> => {
  await submitQuestionnaireFunction({
    participationId,
    activityName,
    userTimezone: getTimezoneName(),
    traceProperties: {
      ...traceProperties,
      traceClientActionType: 'screen-finish',
    },
  });
};

/**
 * Persists the post-submission result context for re-entry (fields/calculations
 * are rules-locked once 'post-questionnaire'), enters the post-questionnaire
 * phase, and clears the loading state.
 */
export const enterPostQuestionnairePhase = async (
  dispatch: (action: { type: string; payload?: unknown }) => void,
  {
    userId,
    participationId,
    activityName,
    output,
    traceProperties,
  }: {
    userId: string;
    participationId: string;
    activityName: string;
    output: ProsePostQuestionnaireOutput;
    traceProperties: Omit<DbTraceProperties, 'traceClientActionType'>;
  }
): Promise<void> => {
  await writeQuestionnaireFinishData(
    userId,
    participationId,
    activityName,
    {
      contextData: output.contextData,
      fieldsData: undefined,
      calculationsData: undefined,
      pageIndex: 0,
    },
    traceProperties,
    'screen-post-questionnaire-enter'
  );

  dispatch(enterPostQuestionnaire(output));
  dispatch(setLoading(false));
};
